import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  AnesthesiaRecordSchema,
  SaveAnesthesiaDraftSchema,
  SignAnesthesiaSchema,
  type AnesthesiaRecord,
  type ProcedureId,
  type SaveAnesthesiaDraftInput,
  type SignAnesthesiaInput,
} from '@datazo/contracts';
import { ApiClientError, apiFetch } from '@/shared/api/client';

/**
 * Hooks de acceso a `/api/anesthesia`. Patrón espejo de `useConsentByProcedure`
 * — el registro de anestesia es legalmente vinculante, así que validamos toda
 * respuesta contra el schema de Zod.
 */

export const anesthesiaKeys = {
  all: ['anesthesia'] as const,
  byProcedure: (id: ProcedureId) => ['anesthesia', id] as const,
};

/**
 * Devuelve el registro de anestesia firmado del episodio, o `null` si aún no
 * existe (404). El caso "no firmado todavía" es esperado y NO debe disparar
 * el camino de error de la UI.
 */
export function useAnesthesiaByProcedure(
  procedimientoId: ProcedureId | undefined,
): UseQueryResult<AnesthesiaRecord | null, Error> {
  return useQuery({
    queryKey:
      procedimientoId !== undefined
        ? anesthesiaKeys.byProcedure(procedimientoId)
        : (['anesthesia', '__missing__'] as const),
    queryFn: async (): Promise<AnesthesiaRecord | null> => {
      if (procedimientoId === undefined) {
        throw new Error('procedimientoId requerido');
      }
      try {
        return await apiFetch<AnesthesiaRecord>(
          `/api/anesthesia/by-procedure/${encodeURIComponent(procedimientoId)}`,
          { parseAs: AnesthesiaRecordSchema },
        );
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: procedimientoId !== undefined,
    staleTime: 30_000,
  });
}

export function useSignAnesthesia(): UseMutationResult<
  AnesthesiaRecord,
  Error,
  SignAnesthesiaInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SignAnesthesiaInput) => {
      const body = SignAnesthesiaSchema.parse(input);
      return apiFetch<AnesthesiaRecord>('/api/anesthesia/sign', {
        method: 'POST',
        body,
        parseAs: AnesthesiaRecordSchema,
      });
    },
    onSuccess: (created) => {
      qc.setQueryData(anesthesiaKeys.byProcedure(created.procedimientoId), created);
      void qc.invalidateQueries({
        queryKey: anesthesiaKeys.byProcedure(created.procedimientoId),
      });
      void qc.invalidateQueries({ queryKey: ['procedures'] });
      void qc.invalidateQueries({ queryKey: ['procedures', created.procedimientoId] });
      void qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

/** Preguardado del borrador (sin firma). No transiciona el episodio. */
export function useSaveAnesthesiaDraft(): UseMutationResult<
  AnesthesiaRecord,
  Error,
  SaveAnesthesiaDraftInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveAnesthesiaDraftInput) => {
      const body = SaveAnesthesiaDraftSchema.parse(input);
      return apiFetch<AnesthesiaRecord>('/api/anesthesia/draft', {
        method: 'POST',
        body,
        parseAs: AnesthesiaRecordSchema,
      });
    },
    onSuccess: (saved) => {
      qc.setQueryData(anesthesiaKeys.byProcedure(saved.procedimientoId), saved);
      void qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export type { AnesthesiaRecord };
